import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PaymentService from "../../../service/payment-service";
import QuestionService from "../../../service/question-service";
import useAuth from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import BaiyeChat from "./chat/baiye-chat";
import BaiyeTicket from "./ticket/baiye-ticket";
import { questionMessages, isPending } from "./baiye-messages";
import "./baiye-question-page-style.scss";

//提問（白夜）
//
//  一頁兩個功能，用頂部的切換鈕在兩者之間切：
//    chat   跟白夜對話（夾帶命盤等結構化內容）
//    ticket 購買票券
//
//  做成同一頁而不是兩個路由：買券是為了繼續問，兩者來回很頻繁，
//  切分頁比在頁面之間跳來跳去順。
//
//  —— 為什麼跟問卦（伏爻子）各自一包 ——
//
//  原本這兩個底部頁籤共用同一支元件，只靠 persona 設定區分「誰回答你」——
//  那時兩邊的畫面確實一模一樣，共用是對的。
//
//  現在不是了：問卦長成一整套儀式（歡迎動畫 → 寫問題 → 覆述動畫 → 付款
//  → 注意事項 → 搖龜殼 → 卜卦動畫 → 斷語進聊天室），而提問仍然只是對話。
//  硬共用會讓那支元件長滿「只有問卦才要做」的分支，兩邊都更難改。
//
//  所以拆成 pages/fuyaozi-ask/（伏爻子）與 pages/baiye-question/（白夜），
//  連 CSS class 也各自一套（.baiye-question__ / .baiye-chat__ / .baiye-ticket__）——
//  共用 class 等於沒拆：改一邊的樣式會悄悄動到另一邊。
//
//  —— 券 ——
//  問事券（type "ask"）只能問白夜；問卦券（"gua"）只能問伏爻子。
//  原本是同一個計數器，兩頁拆開之後跟著分開（見 models/user 的說明）。
//
//  ⚠️ 餘額要由這一頁取、往下傳給聊天 —— 聊天那支用它決定「能不能送出」。
//  原本沒傳（remainCount 吃預設值 0），所以送出永遠被攔去購券頁，
//  提問這一頁其實從來沒有真的能問過。2026-10-07 修掉。
//—— 輪詢的節奏 ——
//  背景生成實測約 7.5 秒。2 秒一次、最多 15 次（30 秒）——
//  正常情況第四次就拿到了，這組數字是給例外情況用的（模型慢、網路卡）。
//  超過就交給 isPending 的三分鐘界線處理（見 baiye-messages）。
const ANSWER_POLL_MS = 2000;
const ANSWER_MAX_TRIES = 15;

const BaiyeQuestionPage = () => {
  const [tab, setTab] = useState("chat");
  const { isLoggedIn } = useAuth();

  //—— 這一層持有的是「提問」，不是「訊息」 ——
  //
  //  訊息由提問推導（questionMessages），不另外存一份。
  //  存訊息的話，回答輪詢回來時要去訊息陣列裡找那一則換掉、分頁往前接時
  //  又要再接一次 —— 同一件事有兩個真相，兩邊遲早會分岔。
  //
  //  順序是舊的在前、新的在後，跟畫面由上往下讀的順序一致。
  const [history, setHistory] = useState([]);
  const messages = useMemo(() => history.flatMap(questionMessages), [history]);

  //歷史拉完了沒。沒拉完就不要畫空狀態 ——
  //  問過二十題的人一進來會先看到「就著你的命盤問」再被歷史蓋掉，閃一下很糟。
  const [historyReady, setHistoryReady] = useState(false);
  //往回翻的游標（上一頁最舊那一題的 createdAt）與「還有沒有更舊的」
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  //送出失敗的原因。顯示在輸入列正上方
  const [askError, setAskError] = useState("");

  //—— 他是被券擋下來才過來的嗎 ——
  //
  //  買完要不要把人送回對話，看的是這個。只有被帶過來的人才送 ——
  //  自己切過來囤券的人沒有未完成的事，把他彈回對話等於替他決定
  //  接下來要做什麼，而他可能只是想再買一包。
  //
  //  用 ref 不用 state：它只在事件處理器裡讀寫，不影響畫面，
  //  做成 state 只會多觸發幾次重繪。
  const sentToBuyRef = useRef(false);

  //—— 有一則回答是他還沒看到的 ——
  //
  //  白夜要跑十幾秒，使用者在等的時候跑去買券是合理的行為。
  //  答案在那段時間產好的話，切換鈕上要有東西告訴他「可以回去看了」。
  //
  //  這件事不用等後端的未讀數：答案是 fillAnswer 填進來的，
  //  那一刻人在哪一個分頁，前端自己就知道。
  const [unread, setUnread] = useState(false);

  //  fillAnswer 是 useCallback([]) —— 依賴是空的，所以它閉包裡的 tab
  //  永遠是第一次 render 的 "chat"，直接讀會判錯。
  //  （把 tab 加進依賴會讓每次切分頁都重建它，正在跑的輪詢鏈會用到舊的那支。）
  //  用一支跟著 tab 走的 ref 取現值。
  const tabRef = useRef("chat");
  useEffect(() => {
    tabRef.current = tab;
  }, [tab]);

  //手動切頁籤就代表那次交棒結束了。
  //  不清的話：被帶去購券頁、自己切回對話放棄了、過幾天又切去買券囤著，
  //  買完會被硬拉回對話。
  const handleSwitchTab = (next) => {
    sentToBuyRef.current = false;
    //  切回對話就算讀過了 —— 那一串就在眼前，再留著紅點只會讓人找不到
    //  到底哪裡還沒看。
    if (next === "chat") setUnread(false);
    setTab(next);
  };
  //null 代表還沒取到。取不到就當 0 —— 寧可把人導去購券頁，
  //  也不要讓他打完字送出才被後端退（那時字已經沒了）。
  const [remain, setRemain] = useState(null);
  const navigate = useNavigate();

  //餘額。進頁取一次，而且每次從購券分頁切回聊天時重取 ——
  //  使用者可能剛買完券回來，顯示舊數字會讓他以為沒買成功。
  //
  //  ⚠️ 沒登入就不要打。P-? 的 tickets 要登入，而 index.js 的攔截器
  //  對**任何** 401 都廣播 auth-expired —— 它分不出「token 過期」和
  //  「根本還沒登入」。所以一個沒登入的人點進提問，會在還沒做任何事
  //  之前就被「登入已逾期」蓋住整頁（Kevin 2026-10-30 回報）。
  //
  //  這一頁刻意不擋在門口（見 App.js 的路由說明）：先讓他進來看到白夜、
  //  看到提問長什麼樣，等他真的要打字才跳登入。那個攔截點在 BaiyeChat
  //  的 handleFocus，不是這裡。
  //
  //  isLoggedIn 進依賴陣列：登入完成之後它會翻，這支會自己再跑一次把餘額補上。
  const loadTickets = useCallback(() => {
    if (!isLoggedIn) {
      setRemain(0);
      return;
    }
    PaymentService.tickets()
      .then((res) => setRemain(res.data?.data?.tickets?.ask ?? 0))
      .catch(() => setRemain(0));
  }, [isLoggedIn]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  //切到購券分頁要回到頂端。
  //  聊天分頁進場會自動捲到最新訊息，不重置的話切過來時頁面還停在下面，
  //  「購買問事券」的標題會被頂部列蓋住。
  //  切回聊天則不處理 —— BaiyeChat 自己會捲到最新訊息。
  useEffect(() => {
    if (tab === "ticket") window.scrollTo(0, 0);
    else loadTickets();
  }, [tab, loadTickets]);

  //—— 輪詢一題的回答 ——
  //
  //  先讓它以 typing 的樣子待在串裡（見 baiye-messages 的 masterMessage），
  //  拿到回答再就地換掉那一筆 —— 訊息是從提問推導的，換掉提問，
  //  那一組問答自己就會從三個點變成白夜的回答。
  const fillAnswer = useCallback((questionID, tries = 0) => {
    const retry = () => {
      if (tries < ANSWER_MAX_TRIES) {
        setTimeout(() => fillAnswer(questionID, tries + 1), ANSWER_POLL_MS);
      }
    };

    QuestionService.get(questionID)
      .then(({ data }) => {
        const q = data?.data?.question;
        //還在跑 → 下一輪再問。用 isPending 而不是只看 status，
        //  不然 done 但 answer 是空字串的那種會被當成已完成。
        if (!q || isPending(q)) return retry();

        setHistory((prev) =>
          prev.map((x) => (x.questionID === questionID ? q : x)),
        );

        //  人不在對話分頁 → 這一則他沒看到。
        //  在對話分頁就不要標：訊息已經在畫面上了，標了反而是假的未讀。
        if (tabRef.current !== "chat") setUnread(true);
      })
      //查不到或網路斷一下都不要停，下一輪再試
      .catch(retry);
  }, []);

  //—— 進頁面拉聊天串 ——
  //
  //  訊息沒有存在前端任何地方：重整、換裝置、換瀏覽器都要看得到同一串，
  //  所以唯一的來源是 Q-1。本機存一份只會變成「這台看得到、那台看不到」。
  //
  //  未登入不打（Q-1 要登入）。登入攔截在輸入框 focus 的時候才跳 ——
  //  先讓他進來看到角色，所以這裡要能安靜地什麼都不做。
  useEffect(() => {
    if (!isLoggedIn) {
      setHistory([]);
      setHasMore(false);
      setHistoryReady(true);
      return undefined;
    }

    //  alive 要在 effect 裡面設成 true，不能寫 useRef(true)。
    //  StrictMode 下是「掛載 → cleanup → 再掛載」，而 useRef 的初始值
    //  第二次掛載不會重新執行 —— 那樣第二次之後永遠是 false（CLAUDE.md）。
    let alive = true;

    QuestionService.list()
      .then(({ data }) => {
        if (!alive) return;
        const list = data?.data?.questions || [];

        //  後端是「新的在前」（往回翻才撈得動），
        //  但聊天室是由上往下讀時間，所以這裡翻過來。
        const ordered = [...list].reverse();
        setHistory(ordered);
        setCursor(data?.data?.nextBefore || null);
        setHasMore(Boolean(data?.data?.hasMore));

        //  離開時還在生成的那幾題要接著輪詢 ——
        //  不接的話它們會永遠停在三個點，而回答其實早就寫好了。
        ordered.filter(isPending).forEach((q) => fillAnswer(q.questionID));
      })
      //拉不到就當作沒有歷史。這一頁的主要功能是問新的一題，
      //  為了歷史擋住整頁不值得。
      .catch(() => {})
      .finally(() => {
        if (alive) setHistoryReady(true);
      });

    return () => {
      alive = false;
    };
  }, [isLoggedIn, fillAnswer]);

  //—— 往回翻一頁（捲到最上面時觸發）——
  //  接在最前面，而捲軸位置要留在原地 —— 那一段在聊天室元件裡做
  //  （只有它知道畫面高度變了多少）。
  const handleLoadMore = useCallback(() => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);

    QuestionService.list(cursor)
      .then(({ data }) => {
        const list = data?.data?.questions || [];
        const ordered = [...list].reverse();
        setHistory((prev) => [...ordered, ...prev]);
        setCursor(data?.data?.nextBefore || null);
        setHasMore(Boolean(data?.data?.hasMore));
        ordered.filter(isPending).forEach((q) => fillAnswer(q.questionID));
      })
      //  翻不動就讓它停在那裡。往回看是附加的，失敗不該影響問新的一題。
      .catch(() => setHasMore(false))
      .finally(() => setLoadingMore(false));
  }, [cursor, loadingMore, fillAnswer]);

  //—— 送出一題 ——
  //
  //  後端會原子扣券、排盤、建一筆，然後背景作答（見 routes/question-route Q-3）。
  //  成功就把那一筆接到串尾 —— 它的 answer 還是空的，所以畫面上會是
  //  使用者的問題加三個點，接著輪詢把回答填進去。
  const handleAsk = useCallback(
    (question) => {
      setAskError("");

      QuestionService.create(question)
        .then(({ data }) => {
          if (!data?.status) {
            //  沒券要導去購券分頁，不是顯示一行紅字 ——
            //  前端已經先用餘額擋過一次，走到這裡代表那份餘額是舊的。
            if (String(data?.message || "").includes("券")) {
              sentToBuyRef.current = true;
              setTab("ticket");
              return;
            }
            setAskError(data?.message || "送不出去，請再試一次。");
            return;
          }

          const q = data.data?.question;
          if (!q) return;

          setHistory((prev) => [...prev, q]);
          //  餘額後端帶回來了，不用再打一次 /tickets
          //  （那樣有時序問題：兩支請求並行時可能顯示成扣之前的數字）
          if (typeof data.data?.remain === "number")
            setRemain(data.data.remain);
          fillAnswer(q.questionID);
        })
        .catch((e) =>
          setAskError(e?.response?.data?.message || "送不出去，請再試一次。"),
        );
    },
    [fillAnswer],
  );

  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/", { replace: true });
  };

  return (
    <main className="baiye-question">
      <header className="baiye-question__header">
        <button
          type="button"
          className="baiye-question__back"
          onClick={handleBack}
          aria-label="返回"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M15 5l-7 7 7 7"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div
          className="baiye-question__switch"
          role="tablist"
          aria-label="提問功能"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === "ticket"}
            aria-label="購買票券"
            className={
              tab === "ticket"
                ? "baiye-question__switch-btn is-active"
                : "baiye-question__switch-btn"
            }
            onClick={() => handleSwitchTab("ticket")}
          >
            {/* 票券。兩側的半圓凹口加中間那排虛線（撕票的齒孔）——
                凹口自己就認得出是票，但在 20px 下只剩一個圓角方塊，
                虛線是讓它一眼看出來的那一筆。
                凹口半徑取 2 不取 3：3 在 13 單位高的票上會吃掉整個高度，
                兩個凹口幾乎對穿，整個圖形變成線軸。 */}
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <g
                stroke="currentColor"
                strokeWidth="1.8"
                fill="none"
                strokeLinejoin="round"
                strokeLinecap="round"
              >
                <path d="M2.5 10V7.5a2 2 0 012-2h15a2 2 0 012 2V10a2 2 0 000 4v2.5a2 2 0 01-2 2h-15a2 2 0 01-2-2V14a2 2 0 000-4z" />
                <path d="M15 7v1.6M15 11.2v1.6M15 15.4v1.6" />
              </g>
            </svg>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={tab === "chat"}
            aria-label="對話"
            className={
              tab === "chat"
                ? "baiye-question__switch-btn is-active"
                : "baiye-question__switch-btn"
            }
            onClick={() => handleSwitchTab("chat")}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path
                d="M20 15a3 3 0 01-3 3H9l-4 3v-3H7a3 3 0 01-3-3V7a3 3 0 013-3h10a3 3 0 013 3z"
                stroke="currentColor"
                strokeWidth="1.8"
                fill="none"
                strokeLinejoin="round"
              />
            </svg>

            {/* 有一則回答是在他離開對話分頁時產好的。
                aria-hidden 要拿掉 —— 它現在帶著資訊了，
                讀螢幕的人也該知道有新回答（用 title 而不是視覺文字，
                那顆鈕本身只有 20px，塞不下字）。 */}
            {unread && (
              <i className="baiye-question__dot" title="有新的回答" />
            )}
          </button>
        </div>

        {/* 佔位讓切換鈕真正置中，不被返回鍵擠偏 */}
        <span className="baiye-question__spacer" aria-hidden="true" />
      </header>

      {tab === "chat" ? (
        <BaiyeChat
          messages={messages}
          onSend={handleAsk}
          error={askError}
          //歷史還沒拉回來之前不要畫空狀態（見 BaiyeChat 的說明）
          ready={historyReady}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={handleLoadMore}
          remainCount={remain ?? 0}
          //沒券就把人帶到購券分頁，不用自己找。
          //  記一筆「他是被帶過來的」—— 買完要不要送他回對話，看的是這個。
          onNeedTicket={() => {
            sentToBuyRef.current = true;
            setTab("ticket");
          }}
        />
      ) : (
        //  只有「被券擋下來才過來」的人，買完才送回對話。
        //  自己從頁籤切過來囤券的人沒有未完成的事，把他彈回對話等於
        //  替他決定接下來要做什麼 —— 而他可能只是想再買一包。
        //
        //  （綠界開在新分頁，所以這一頁一直活著，ref 記得住。）
        <BaiyeTicket
          onBought={() => {
            if (!sentToBuyRef.current) return;
            sentToBuyRef.current = false;
            setTab("chat");
          }}
        />
      )}
    </main>
  );
};

export default BaiyeQuestionPage;
